import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';

const HIDDEN_KEYS = new Set(['_id', 'password', 'password_hash', 'hash']);

const humanize = (key) =>
  key
    .replace(/_/g, ' ')
    .replace(/\bid\b/gi, 'ID')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const renderValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (typeof value[0] === 'object' && value[0] !== null) {
      // Array of objects -> compact sub-table
      const cols = Object.keys(value[0]).filter((k) => !HIDDEN_KEYS.has(k));
      return (
        <div className="overflow-x-auto rounded border border-slate-200 mt-1">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>{cols.map((c) => <th key={c} className="text-left px-2 py-1 font-medium text-slate-600">{humanize(c)}</th>)}</tr>
            </thead>
            <tbody>
              {value.map((row, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {cols.map((c) => <td key={c} className="px-2 py-1 text-slate-700">{String(row[c] ?? '—')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return value.join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Generic read-only record viewer. Auto-renders a record object's fields as
 * label/value pairs (arrays of objects become compact sub-tables). Used to give
 * view-only (read) users a way to inspect a record without the edit form.
 *
 * Props:
 *   open, onOpenChange  - dialog control
 *   title               - dialog title
 *   record              - object to display (null hides dialog)
 *   fieldOrder          - optional array of keys to control order/whitelist
 *   labels              - optional map { key: 'Custom Label' }
 */
export const RecordViewDialog = ({ open, onOpenChange, title = 'View Record', record, fieldOrder, labels = {} }) => {
  if (!record) return null;
  let keys = fieldOrder && fieldOrder.length
    ? fieldOrder
    : Object.keys(record).filter((k) => !HIDDEN_KEYS.has(k));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="record-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            <Badge variant="outline" className="text-[10px] font-normal text-slate-500">Read-only</Badge>
          </DialogTitle>
          <DialogDescription>You have view-only access to this record.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 py-2">
          {keys.map((k) => {
            const isComplex = Array.isArray(record[k]) || (record[k] && typeof record[k] === 'object');
            return (
              <div key={k} className={isComplex ? 'sm:col-span-2' : ''}>
                <p className="text-xs font-medium text-slate-500">{labels[k] || humanize(k)}</p>
                <div className="text-sm text-slate-900 break-words">{renderValue(record[k])}</div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecordViewDialog;
